import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLazyQuery } from '@apollo/client';
import Autocomplete, { IAutocompleteOption } from '../common/autocomplete.tsx';
import {
  cn,
  createXlsxExportUrl,
  getAutocompleteKey,
  getAutocompleteTitle,
  orderToLetter,
} from '../../utils/common-utils.ts';
import {
  useGetQuizViewData,
  useGetTestViewData,
} from './answers-view-hooks.ts';
import {
  AttemptHistoryItem,
  GET_QUIZ_FIRST_ATTEMPTS,
  GET_QUIZ_ATTEMPT_BY_ID,
  GET_QUIZ_ATTEMPT_HISTORY,
  GET_TEST_ATTEMPT_BY_ID,
  GET_TEST_FIRST_ATTEMPTS,
  GET_TEST_ATTEMPT_HISTORY,
  QuizViewAttempt,
  TestViewAttempt,
} from '../../apollo/answers-view.ts';
import Loading from '../common/loading.tsx';
import Button from '../common/button.tsx';
import Table from '../table/table.tsx';
import {
  QuestionAnswerType,
  TestAttemptModuleLevel,
  TestSectionModuleLevel,
} from '../../apollo/__generated__/graphql.ts';
import { transformRatioToColor } from '../../utils/transform-ratio-to-color.ts';
import { TypeSelect } from './type-select.tsx';
import { ElementType } from '../../utils/enums.ts';
import { ElementSelect } from './element-select.tsx';
import { CourseSelect } from './course-select.tsx';
import { AnswersUserSelect } from './answers-user-select.tsx';
import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ArrowPathIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';

interface IProps {
  courseId?: number;
}

const columnsOptions: string[] = [
  'QID',
  'Ans.',
  'Domain',
  'Skill',
  'Subject',
  'Type',
  'Performance',
];

const usersCollator = new Intl.Collator(undefined, {
  sensitivity: 'base',
  numeric: true,
});

const sortUsers = (nextUsers: IAutocompleteOption[]) =>
  [...nextUsers].sort((leftUser, rightUser) => {
    const leftTitle = getAutocompleteTitle(leftUser) || '';
    const rightTitle = getAutocompleteTitle(rightUser) || '';
    const titleComparison = usersCollator.compare(leftTitle, rightTitle);

    if (titleComparison !== 0) {
      return titleComparison;
    }

    return usersCollator.compare(
      getAutocompleteKey(leftUser) || '',
      getAutocompleteKey(rightUser) || '',
    );
  });

const getOrderedColumns = (nextColumns: string[]) => {
  return columnsOptions.filter((column) => nextColumns.includes(column));
};

const formatPercent = (value?: number | null) => {
  return Number.isFinite(value) ? `${Math.round(value!)}%` : '0%';
};

const formatAttemptTimestamp = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

type AttemptMode = 'latest' | 'first' | 'custom';

const userColumnClassName = 'w-[3.1rem] min-w-[3.1rem] max-w-[3.1rem] align-top';
const userAnswerCellClassName =
  'w-[3.1rem] min-w-[3.1rem] max-w-[3.1rem] px-0.5 text-center text-xs';
const answerColumnClassName =
  'w-[3.25rem] min-w-[3.25rem] max-w-[3.25rem] text-center [&>div]:justify-center [&>div]:text-center';
const performanceColumnClassName =
  'w-[4.5rem] min-w-[4.5rem] max-w-[4.5rem] text-center [&>div]:justify-center [&>div]:text-center';

const getColumnTitle = (column: string) =>
  column === 'Performance' ? 'Perf.' : column;

const getColumnClassName = (column: string) => {
  switch (column) {
    case 'Ans.':
      return answerColumnClassName;
    case 'Performance':
      return performanceColumnClassName;
    default:
      return undefined;
  }
};

const getModuleHeaderClassName = (sectionName?: string) =>
  sectionName === 'math'
    ? 'text-md bg-[#990000] px-4 text-left font-medium text-white'
    : 'text-md bg-[#4a86e8] px-4 text-left font-medium text-white';

const AnswersView = ({ courseId }: IProps) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [selectedModules, setSelectedModules] = useState<string[]>();
  const [columns, setColumns] = useState<string[]>([
    'Ans.',
    'Domain',
    'Performance',
  ]);
  const [type, setType] = useState<ElementType>(ElementType.QUIZ);
  const [element, setElement] = useState<IAutocompleteOption>();
  const [course, setCourse] = useState<IAutocompleteOption>();
  const [users, setUsers] = useState<IAutocompleteOption[]>([]);
  const [loadedUsers, setLoadedUsers] = useState<IAutocompleteOption[]>([]);
  const [quizAttemptHistoryByUserId, setQuizAttemptHistoryByUserId] = useState<
    Record<number, AttemptHistoryItem[]>
  >({});
  const [testAttemptHistoryByUserId, setTestAttemptHistoryByUserId] = useState<
    Record<number, AttemptHistoryItem[]>
  >({});
  const [quizAttemptDetailsById, setQuizAttemptDetailsById] = useState<
    Record<number, QuizViewAttempt>
  >({});
  const [testAttemptDetailsById, setTestAttemptDetailsById] = useState<
    Record<number, TestViewAttempt>
  >({});
  const [quizAttemptOverridesByUserId, setQuizAttemptOverridesByUserId] = useState<
    Record<number, QuizViewAttempt | undefined>
  >({});
  const [testAttemptOverridesByUserId, setTestAttemptOverridesByUserId] = useState<
    Record<number, TestViewAttempt | undefined>
  >({});
  const [attemptMode, setAttemptMode] = useState<AttemptMode>('latest');
  const [openAttemptMenuUserId, setOpenAttemptMenuUserId] = useState<number>();
  const [loadingAttemptHistoryUserId, setLoadingAttemptHistoryUserId] =
    useState<number>();
  const [loadingAttemptSelectionUserId, setLoadingAttemptSelectionUserId] =
    useState<number>();
  const [loadingAttemptMode, setLoadingAttemptMode] = useState<AttemptMode>();
  const attemptMenuRef = useRef<HTMLDivElement>(null);

  const currentCourseId =
    courseId || (course ? Number(getAutocompleteKey(course)) : undefined);

  const quizId = useMemo(
    () =>
      element && type === ElementType.QUIZ
        ? Number(getAutocompleteKey(element))
        : undefined,
    [element, type],
  );

  const testId = useMemo(
    () =>
      element && type === ElementType.TEST
        ? Number(getAutocompleteKey(element))
        : undefined,
    [element, type],
  );

  const loadedUserIds: number[] = useMemo(() => {
    return loadedUsers?.map((user) => Number(getAutocompleteKey(user))) || [];
  }, [loadedUsers]);

  const orderedUsers = useMemo(() => {
    if (!loadedUsers.length) {
      return users;
    }

    const selectedKeys = new Set(users.map((user) => getAutocompleteKey(user)));
    return loadedUsers.filter((user) => selectedKeys.has(getAutocompleteKey(user)));
  }, [loadedUsers, users]);

  const orderedUserIds: number[] = useMemo(() => {
    return orderedUsers?.map((user) => Number(getAutocompleteKey(user))) || [];
  }, [orderedUsers]);

  const orderedColumns = useMemo(() => getOrderedColumns(columns), [columns]);

  const {
    quizQuestionsSort,
    quizAttempts,
    completedAttemptsCounts: quizAttemptCounts,
    quizPerformance,
    loading: loadingQuiz,
    refetch: refetchQuiz,
  } = useGetQuizViewData(
    quizId,
    loadedUserIds,
    orderedUserIds,
    quizAttemptOverridesByUserId,
  );

  const {
    test,
    testAttempts,
    modulesOptions,
    completedAttemptsCounts: testAttemptCounts,
    testPerformance,
    loading: loadingTest,
    refetch: refetchTest,
  } = useGetTestViewData(
    testId,
    loadedUserIds,
    orderedUserIds,
    testAttemptOverridesByUserId,
  );

  const [loadQuizAttemptHistory] = useLazyQuery(GET_QUIZ_ATTEMPT_HISTORY, {
    fetchPolicy: 'no-cache',
  });

  const [loadTestAttemptHistory] = useLazyQuery(GET_TEST_ATTEMPT_HISTORY, {
    fetchPolicy: 'no-cache',
  });

  const [loadQuizAttemptById] = useLazyQuery(GET_QUIZ_ATTEMPT_BY_ID, {
    fetchPolicy: 'no-cache',
  });

  const [loadTestAttemptById] = useLazyQuery(GET_TEST_ATTEMPT_BY_ID, {
    fetchPolicy: 'no-cache',
  });

  const [loadQuizFirstAttempts] = useLazyQuery(GET_QUIZ_FIRST_ATTEMPTS, {
    fetchPolicy: 'no-cache',
  });

  const [loadTestFirstAttempts] = useLazyQuery(GET_TEST_FIRST_ATTEMPTS, {
    fetchPolicy: 'no-cache',
  });

  const onChangeUsers = useCallback((nextUsers: IAutocompleteOption[]) => {
    setUsers(sortUsers(nextUsers));
    setLoadedUsers((prevUsers) => {
      const prevKeys = new Set(prevUsers.map((user) => getAutocompleteKey(user)));
      const nextLoadedUsers = nextUsers.filter((user) => {
        const key = getAutocompleteKey(user);
        return !!key && !prevKeys.has(key);
      });

      return nextLoadedUsers.length
        ? sortUsers([...prevUsers, ...nextLoadedUsers])
        : prevUsers;
    });
  }, []);

  useEffect(() => {
    if (typeof selectedModules === 'undefined' && modulesOptions.length)
      setSelectedModules(modulesOptions);
  }, [modulesOptions, selectedModules]);

  useEffect(() => {
    const closeAttemptMenu = (event: MouseEvent) => {
      if (
        attemptMenuRef.current &&
        !attemptMenuRef.current.contains(event.target as Node)
      ) {
        setOpenAttemptMenuUserId(undefined);
      }
    };

    document.addEventListener('mousedown', closeAttemptMenu);
    return () => document.removeEventListener('mousedown', closeAttemptMenu);
  }, []);

  const onChangeType = useCallback((type: ElementType) => {
    setType(type);
    setElement(undefined);
    setUsers([]);
    setLoadedUsers([]);
    setQuizAttemptHistoryByUserId({});
    setTestAttemptHistoryByUserId({});
    setQuizAttemptDetailsById({});
    setTestAttemptDetailsById({});
    setQuizAttemptOverridesByUserId({});
    setTestAttemptOverridesByUserId({});
    setAttemptMode('latest');
    setOpenAttemptMenuUserId(undefined);
    setLoadingAttemptHistoryUserId(undefined);
    setLoadingAttemptSelectionUserId(undefined);
    setLoadingAttemptMode(undefined);
    setSelectedModules(undefined);
  }, []);

  const onChangeElement = useCallback((value?: IAutocompleteOption) => {
    setElement(value);
    setUsers([]);
    setLoadedUsers([]);
    setQuizAttemptHistoryByUserId({});
    setTestAttemptHistoryByUserId({});
    setQuizAttemptDetailsById({});
    setTestAttemptDetailsById({});
    setQuizAttemptOverridesByUserId({});
    setTestAttemptOverridesByUserId({});
    setAttemptMode('latest');
    setOpenAttemptMenuUserId(undefined);
    setLoadingAttemptHistoryUserId(undefined);
    setLoadingAttemptSelectionUserId(undefined);
    setLoadingAttemptMode(undefined);
    setSelectedModules(undefined);
  }, []);

  const onChangeCourse = useCallback((value?: IAutocompleteOption) => {
    if (getAutocompleteKey(value) === getAutocompleteKey(course)) {
      return;
    }
    setCourse(value);
    setElement(undefined);
    setUsers([]);
    setLoadedUsers([]);
    setQuizAttemptHistoryByUserId({});
    setTestAttemptHistoryByUserId({});
    setQuizAttemptDetailsById({});
    setTestAttemptDetailsById({});
    setQuizAttemptOverridesByUserId({});
    setTestAttemptOverridesByUserId({});
    setAttemptMode('latest');
    setOpenAttemptMenuUserId(undefined);
    setLoadingAttemptHistoryUserId(undefined);
    setLoadingAttemptSelectionUserId(undefined);
    setLoadingAttemptMode(undefined);
    setSelectedModules(undefined);
  }, [course]);

  const removeUser = useCallback((userKey?: string) => {
    if (!userKey) return;
    setUsers((prevUsers) =>
      prevUsers.filter((user) => getAutocompleteKey(user) !== userKey),
    );
  }, []);

  const onChangeColumns = (e: IAutocompleteOption | IAutocompleteOption[]) => {
    const isArray = Array.isArray(e);
    const key = getAutocompleteKey(isArray ? e[0] : e);
    if (!key || !isArray) return setColumns([]);
    setColumns(e as string[]);
  };

  const onChangeModulesColumns = (
    e: IAutocompleteOption | IAutocompleteOption[],
  ) => {
    const isArray = Array.isArray(e);
    const key = getAutocompleteKey(isArray ? e[0] : e);
    if (!key || !isArray) return setSelectedModules([]);
    setSelectedModules(e as string[]);
  };

  const getAttemptHistory = useCallback(
    (userId: number) => {
      return type === ElementType.QUIZ
        ? quizAttemptHistoryByUserId[userId]
        : testAttemptHistoryByUserId[userId];
    },
    [quizAttemptHistoryByUserId, testAttemptHistoryByUserId, type],
  );

  const getCurrentAttemptId = useCallback(
    (userId: number) => {
      return type === ElementType.QUIZ
        ? quizAttempts.find((attempt) => attempt.userId === userId)?.attemptId
        : testAttempts.find((attempt) => attempt.userId === userId)?.attemptId;
    },
    [quizAttempts, testAttempts, type],
  );

  const getTotalAttemptCount = useCallback(
    (userId: number) => {
      return type === ElementType.QUIZ
        ? quizAttemptCounts[userId]
        : testAttemptCounts[userId];
    },
    [quizAttemptCounts, testAttemptCounts, type],
  );

  const getAttemptCount = useCallback(
    (userId: number) => {
      const history = getAttemptHistory(userId);
      if (history?.length) {
        const currentAttemptId = getCurrentAttemptId(userId);
        const currentIndex = history.findIndex(
          (attempt) => attempt.id === currentAttemptId,
        );

        return currentIndex >= 0 ? currentIndex + 1 : history.length;
      }

      const totalAttemptCount = getTotalAttemptCount(userId);

      if (!totalAttemptCount) {
        return undefined;
      }

      if (attemptMode === 'first') {
        return 1;
      }

      if (attemptMode === 'latest') {
        return totalAttemptCount;
      }

      return totalAttemptCount;
    },
    [
      attemptMode,
      getAttemptHistory,
      getCurrentAttemptId,
      getTotalAttemptCount,
    ],
  );

  const loadAttemptHistory = useCallback(
    async (userId: number) => {
      if (loadingAttemptHistoryUserId === userId) {
        return;
      }

      if (type === ElementType.QUIZ) {
        if (!quizId || quizAttemptHistoryByUserId[userId]) {
          return;
        }

        setLoadingAttemptHistoryUserId(userId);
        try {
          const result = await loadQuizAttemptHistory({
            variables: { input: { id: quizId }, userId },
          });
          setQuizAttemptHistoryByUserId((prev) => ({
            ...prev,
            [userId]: result.data?.quiz.getQuiz.completedAttemptsHistory ?? [],
          }));
        } finally {
          setLoadingAttemptHistoryUserId(undefined);
        }
        return;
      }

      if (!testId || testAttemptHistoryByUserId[userId]) {
        return;
      }

      setLoadingAttemptHistoryUserId(userId);
      try {
        const result = await loadTestAttemptHistory({
          variables: { input: { id: testId }, userId },
        });
        setTestAttemptHistoryByUserId((prev) => ({
          ...prev,
          [userId]: result.data?.test.getTest.completedAttemptsHistory ?? [],
        }));
      } finally {
        setLoadingAttemptHistoryUserId(undefined);
      }
    },
    [
      loadQuizAttemptHistory,
      loadTestAttemptHistory,
      loadingAttemptHistoryUserId,
      quizAttemptHistoryByUserId,
      quizId,
      testAttemptHistoryByUserId,
      testId,
      type,
    ],
  );

  const toggleAttemptMenu = useCallback(
    async (userId: number) => {
      if (openAttemptMenuUserId === userId) {
        setOpenAttemptMenuUserId(undefined);
        return;
      }

      setOpenAttemptMenuUserId(userId);
      await loadAttemptHistory(userId);
    },
    [loadAttemptHistory, openAttemptMenuUserId],
  );

  const selectAttempt = useCallback(
    async (userId: number, attemptNumber: number) => {
      if (type === ElementType.QUIZ) {
        const history = quizAttemptHistoryByUserId[userId] ?? [];
        const selectedAttempt = history[attemptNumber - 1];
        const latestAttemptId = history.at(-1)?.id;

        if (!selectedAttempt || !latestAttemptId) {
          setOpenAttemptMenuUserId(undefined);
          return;
        }

        if (selectedAttempt.id === latestAttemptId) {
          setQuizAttemptOverridesByUserId((prev) => {
            const rest = { ...prev };
            delete rest[userId];
            return rest;
          });
          setAttemptMode('custom');
          setOpenAttemptMenuUserId(undefined);
          return;
        }

        const cachedAttempt = quizAttemptDetailsById[selectedAttempt.id];
        if (cachedAttempt) {
          setQuizAttemptOverridesByUserId((prev) => ({
            ...prev,
            [userId]: cachedAttempt,
          }));
          setAttemptMode('custom');
          setOpenAttemptMenuUserId(undefined);
          return;
        }

        setLoadingAttemptSelectionUserId(userId);
        try {
          const result = await loadQuizAttemptById({
            variables: { input: { id: selectedAttempt.id } },
          });
          const fetchedAttempt = result.data?.quizAttempt.getAttempt;

          if (!fetchedAttempt?.createdBy?.id) {
            return;
          }

          setQuizAttemptDetailsById((prev) => ({
            ...prev,
            [fetchedAttempt.id]: fetchedAttempt,
          }));
          setQuizAttemptOverridesByUserId((prev) => ({
            ...prev,
            [userId]: fetchedAttempt,
          }));
          setAttemptMode('custom');
        } finally {
          setLoadingAttemptSelectionUserId(undefined);
        }
      } else {
        const history = testAttemptHistoryByUserId[userId] ?? [];
        const selectedAttempt = history[attemptNumber - 1];
        const latestAttemptId = history.at(-1)?.id;

        if (!selectedAttempt || !latestAttemptId) {
          setOpenAttemptMenuUserId(undefined);
          return;
        }

        if (selectedAttempt.id === latestAttemptId) {
          setTestAttemptOverridesByUserId((prev) => {
            const rest = { ...prev };
            delete rest[userId];
            return rest;
          });
          setAttemptMode('custom');
          setOpenAttemptMenuUserId(undefined);
          return;
        }

        const cachedAttempt = testAttemptDetailsById[selectedAttempt.id];
        if (cachedAttempt) {
          setTestAttemptOverridesByUserId((prev) => ({
            ...prev,
            [userId]: cachedAttempt,
          }));
          setAttemptMode('custom');
          setOpenAttemptMenuUserId(undefined);
          return;
        }

        setLoadingAttemptSelectionUserId(userId);
        try {
          const result = await loadTestAttemptById({
            variables: { input: { id: selectedAttempt.id } },
          });
          const fetchedAttempt = result.data?.testAttempt.getAttempt;

          if (!fetchedAttempt?.createdBy?.id) {
            return;
          }

          setTestAttemptDetailsById((prev) => ({
            ...prev,
            [fetchedAttempt.id]: fetchedAttempt,
          }));
          setTestAttemptOverridesByUserId((prev) => ({
            ...prev,
            [userId]: fetchedAttempt,
          }));
          setAttemptMode('custom');
        } finally {
          setLoadingAttemptSelectionUserId(undefined);
        }
      }

      setOpenAttemptMenuUserId(undefined);
    },
    [
      loadQuizAttemptById,
      loadTestAttemptById,
      quizAttemptDetailsById,
      quizAttemptHistoryByUserId,
      testAttemptDetailsById,
      testAttemptHistoryByUserId,
      type,
    ],
  );

  const switchToLatestAttempts = useCallback(() => {
    setAttemptMode('latest');
    setQuizAttemptOverridesByUserId({});
    setTestAttemptOverridesByUserId({});
  }, []);

  const multiAttemptUserIds = useMemo(
    () => loadedUserIds.filter((userId) => (getTotalAttemptCount(userId) ?? 0) > 1),
    [getTotalAttemptCount, loadedUserIds],
  );

  const switchToFirstAttempts = useCallback(async () => {
    if (!loadedUserIds.length) {
      return;
    }

    setLoadingAttemptMode('first');

    try {
      if (type === ElementType.QUIZ) {
        const nextOverrides: Record<number, QuizViewAttempt> = {};

        if (quizId && multiAttemptUserIds.length) {
          const result = await loadQuizFirstAttempts({
            variables: {
              input: { id: quizId },
              attemptsFilter: { userIds: multiAttemptUserIds },
            },
          });

          const fetchedAttempts = (result.data?.quiz.getQuiz.firstCompletedAttempts ??
            []).filter((attempt) => attempt.createdBy?.id);

          fetchedAttempts.forEach((attempt) => {
            nextOverrides[attempt.createdBy!.id] = attempt;
          });

          setQuizAttemptDetailsById((prev) => {
            const next = { ...prev };
            fetchedAttempts.forEach((attempt) => {
              next[attempt.id] = attempt;
            });
            return next;
          });
        }

        setQuizAttemptOverridesByUserId(nextOverrides);
        setTestAttemptOverridesByUserId({});
      } else {
        const nextOverrides: Record<number, TestViewAttempt> = {};

        if (testId && multiAttemptUserIds.length) {
          const result = await loadTestFirstAttempts({
            variables: {
              input: { id: testId },
              attemptsFilter: { userIds: multiAttemptUserIds },
            },
          });

          const fetchedAttempts = (result.data?.test.getTest.firstCompletedAttempts ??
            []).filter((attempt) => attempt.createdBy?.id);

          fetchedAttempts.forEach((attempt) => {
            nextOverrides[attempt.createdBy!.id] = attempt;
          });

          setTestAttemptDetailsById((prev) => {
            const next = { ...prev };
            fetchedAttempts.forEach((attempt) => {
              next[attempt.id] = attempt;
            });
            return next;
          });
        }

        setTestAttemptOverridesByUserId(nextOverrides);
        setQuizAttemptOverridesByUserId({});
      }

      setAttemptMode('first');
    } finally {
      setLoadingAttemptMode(undefined);
    }
  }, [
    loadedUserIds.length,
    loadQuizFirstAttempts,
    loadTestFirstAttempts,
    multiAttemptUserIds,
    quizId,
    testId,
    type,
  ]);

  useEffect(() => {
    if (attemptMode !== 'first' || !loadedUserIds.length) {
      return;
    }

    void switchToFirstAttempts();
  }, [attemptMode, loadedUserIds, switchToFirstAttempts]);

  const getUsersColumns = useCallback(() => {
    return (
      orderedUsers?.reduce<{ title: ReactNode; key: string; className?: string }[]>(
        (acc, user) => {
        const userName = getAutocompleteTitle(user);
        const userId = getAutocompleteKey(user);
        const numericUserId = Number(userId);
        const attemptHistory = userId ? getAttemptHistory(numericUserId) ?? [] : [];
        const attemptCount = userId ? getAttemptCount(numericUserId) : undefined;
        const isAttemptMenuOpen = openAttemptMenuUserId === numericUserId;
        const isAttemptHistoryLoading =
          loadingAttemptHistoryUserId === numericUserId;
        const isAttemptSelectionLoading =
          loadingAttemptSelectionUserId === numericUserId;
        const isAttemptBusy =
          isAttemptHistoryLoading || isAttemptSelectionLoading;

        const attempt = quizAttempts.find((qa) => {
          return Number(qa.userId) === Number(userId);
        });

        if (userId && type === ElementType.QUIZ)
          acc.push({
            title: (
              <div
                className="relative flex w-full flex-col items-center gap-1 text-center text-xs"
                ref={isAttemptMenuOpen ? attemptMenuRef : undefined}
              >
                <div className="flex items-center gap-1">
                  <button
                    aria-label={`Remove ${userName || 'user'}`}
                    className="rounded p-0.5 text-gray transition hover:bg-disabled hover:text-black"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeUser(userId);
                    }}
                    type="button"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className="rounded border border-table-border px-1.5 py-0.5 text-xs font-medium text-blue transition hover:bg-light-blue"
                    onClick={(event) => {
                      event.stopPropagation();
                      void toggleAttemptMenu(numericUserId);
                    }}
                    type="button"
                  >
                    {isAttemptBusy ? '...' : `#${attemptCount ?? '—'}`}
                  </button>
                </div>
                <span
                  className="max-w-full overflow-hidden break-words whitespace-normal leading-4 font-medium [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:4]"
                  title={userName || ''}
                >
                  {userName || ''}
                </span>
                {isAttemptMenuOpen && (
                  <div className="absolute left-0 top-full z-20 mt-1 min-w-[12rem] rounded-md border border-table-border bg-white p-1 shadow-lg">
                    {isAttemptHistoryLoading && (
                      <div className="px-2 py-1 text-xs text-gray">Loading...</div>
                    )}

                    {!isAttemptHistoryLoading && !attemptHistory.length && (
                      <div className="px-2 py-1 text-xs text-gray">
                        No completed attempts
                      </div>
                    )}

                    {!isAttemptHistoryLoading &&
                      [...attemptHistory].reverse().map((historyAttempt, index) => {
                        const attemptNumber = attemptHistory.length - index;
                        const isSelected = historyAttempt.id === attempt?.attemptId;

                        return (
                          <button
                            className={cn(
                              'flex w-full items-center justify-between gap-3 rounded px-2 py-1 text-left text-xs hover:bg-light-blue',
                              isSelected && 'bg-light-blue',
                            )}
                            key={historyAttempt.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              void selectAttempt(numericUserId, attemptNumber);
                            }}
                            type="button"
                          >
                            <span className="font-medium">{`Attempt #${attemptNumber}`}</span>
                            <span className="whitespace-nowrap text-gray">
                              {formatAttemptTimestamp.format(
                                new Date(historyAttempt.createdAt),
                              )}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>
            ),
            key: userId,
            className: userColumnClassName,
          });

        if (userId && type === ElementType.TEST)
          acc.push({
            title: (
              <div
                className="relative flex w-full flex-col items-center gap-1 text-center text-xs"
                ref={isAttemptMenuOpen ? attemptMenuRef : undefined}
              >
                <div className="flex items-center gap-1">
                  <button
                    aria-label={`Remove ${userName || 'user'}`}
                    className="rounded p-0.5 text-gray transition hover:bg-disabled hover:text-black"
                    onClick={(event) => {
                      event.stopPropagation();
                      removeUser(userId);
                    }}
                    type="button"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                  <button
                    className="rounded border border-table-border px-1.5 py-0.5 text-xs font-medium text-blue transition hover:bg-light-blue"
                    onClick={(event) => {
                      event.stopPropagation();
                      void toggleAttemptMenu(numericUserId);
                    }}
                    type="button"
                  >
                    {isAttemptBusy ? '...' : `#${attemptCount ?? '—'}`}
                  </button>
                </div>
                <span
                  className="max-w-full overflow-hidden break-words whitespace-normal leading-4 font-medium [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:4]"
                  title={userName || ''}
                >
                  {userName || ''}
                </span>
                {isAttemptMenuOpen && (
                  <div className="absolute left-0 top-full z-20 mt-1 min-w-[12rem] rounded-md border border-table-border bg-white p-1 shadow-lg">
                    {isAttemptHistoryLoading && (
                      <div className="px-2 py-1 text-xs text-gray">Loading...</div>
                    )}

                    {!isAttemptHistoryLoading && !attemptHistory.length && (
                      <div className="px-2 py-1 text-xs text-gray">
                        No completed attempts
                      </div>
                    )}

                    {!isAttemptHistoryLoading &&
                      [...attemptHistory].reverse().map((historyAttempt, index) => {
                        const attemptNumber = attemptHistory.length - index;
                        const isSelected =
                          historyAttempt.id ===
                          testAttempts.find(
                            (testAttempt) => testAttempt.userId === numericUserId,
                          )?.attemptId;

                        return (
                          <button
                            className={cn(
                              'flex w-full items-center justify-between gap-3 rounded px-2 py-1 text-left text-xs hover:bg-light-blue',
                              isSelected && 'bg-light-blue',
                            )}
                            key={historyAttempt.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              void selectAttempt(numericUserId, attemptNumber);
                            }}
                            type="button"
                          >
                            <span className="font-medium">{`Attempt #${attemptNumber}`}</span>
                            <span className="whitespace-nowrap text-gray">
                              {formatAttemptTimestamp.format(
                                new Date(historyAttempt.createdAt),
                              )}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>
            ),
            key: userId,
            className: userColumnClassName,
          });
        return acc;
      }, []) ?? []
    );
  }, [
      getAttemptCount,
      getAttemptHistory,
      loadingAttemptHistoryUserId,
      loadingAttemptSelectionUserId,
      openAttemptMenuUserId,
      orderedUsers,
    quizAttempts,
    removeUser,
    selectAttempt,
    testAttempts,
    toggleAttemptMenu,
    type,
  ]);

  const getAttemptsForExport = useCallback(
    () =>
      orderedUsers?.reduce<number[]>((acc, user) => {
        const userId = Number(getAutocompleteKey(user));
        let attemptId: number | undefined;
        if (type === ElementType.TEST) {
          attemptId = testAttempts.find(
            (attempt) => attempt.userId === userId,
          )?.attemptId;
        } else {
          attemptId = quizAttempts.find((a) => a.userId === userId)?.attemptId;
        }
        if (attemptId) acc.push(attemptId);
        return acc;
      }, []) || [],
    [orderedUsers, quizAttempts, testAttempts, type],
  );

  const performanceColor = useCallback((performance?: number | null) => {
    return Number.isFinite(performance)
      ? `rgba(${transformRatioToColor(performance! / 100).join(',')}, 0.7)`
      : 'white';
  }, []);

  const exportReport = useCallback(() => {
    const elementId = Number(getAutocompleteKey(element));
    if (!elementId) {
      return;
    }

    const url = createXlsxExportUrl(
      type === ElementType.TEST ? 'test' : 'quiz',
      elementId,
      getAttemptsForExport(),
    );
    window.open(url);
  }, [element, getAttemptsForExport, type]);

  const onRefresh = useCallback(() => {
    switch (type) {
      case ElementType.QUIZ:
        if (quizId && orderedUserIds.length) {
          refetchQuiz();
        }
        break;
      case ElementType.TEST:
        if (testId && orderedUserIds.length) {
          refetchTest();
        }
        break;
    }
  }, [orderedUserIds.length, quizId, refetchQuiz, refetchTest, testId, type]);

  return (
    <div
      className={cn(
        'bg-white transition-all',
        isFullScreen && 'fixed left-0 top-0 h-full w-full bg-white p-4',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <h1>Answers View</h1>
          {(loadingTest || loadingQuiz || !!loadingAttemptMode) && (
            <Loading isSmall={true} />
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            <Button onClick={exportReport} className="h-10 px-4">
              Export
            </Button>
            <Button
              onClick={onRefresh}
              disabled={
                !(testId && orderedUserIds.length) &&
                !(quizId && orderedUserIds.length)
              }
              className="h-10 w-10 p-0"
              tip="Refresh"
              icon={<ArrowPathIcon className="h-4 w-4" />}
            >
            </Button>
            <Button
              red={isFullScreen}
              onClick={() => setIsFullScreen((prevState) => !prevState)}
              className="h-10 w-10 p-0"
              tip={isFullScreen ? 'Disable full screen' : 'Open in full screen'}
              icon={
                isFullScreen ? (
                  <ArrowsPointingInIcon className="h-4 w-4" />
                ) : (
                  <ArrowsPointingOutIcon className="h-4 w-4" />
                )
              }
            >
            </Button>
          </div>

        </div>
      </div>

      <section className="mt-3 flex flex-col gap-3">
        <div className="flex min-w-0 items-center gap-3">
          {!courseId && (
            <div className="w-[22rem] shrink-0">
              <CourseSelect onChange={onChangeCourse} />
            </div>
          )}

          <div className="min-w-[16rem] max-w-[22rem] flex-[0.9]">
            <AnswersUserSelect
              type={type}
              quizId={quizId}
              testId={testId}
              courseId={currentCourseId}
              value={orderedUsers}
              onChange={onChangeUsers}
            />
          </div>

          <div className="flex h-10 shrink-0 items-center gap-3 pl-1 text-sm">
            <span className="font-medium text-gray">Attempts:</span>
            <button
              className={cn(
                'font-medium transition',
                attemptMode === 'latest'
                  ? 'font-bold text-black underline'
                  : 'text-black/50',
              )}
              disabled={!loadedUserIds.length || loadingAttemptMode === 'first'}
              onClick={switchToLatestAttempts}
              type="button"
            >
              Latest
            </button>
            <button
              className={cn(
                'font-medium transition',
                attemptMode === 'first'
                  ? 'font-bold text-black underline'
                  : 'text-black/50',
              )}
              disabled={
                !loadedUserIds.length ||
                loadingAttemptMode === 'first' ||
                (!(quizId && type === ElementType.QUIZ) &&
                  !(testId && type === ElementType.TEST))
              }
              onClick={() => {
                void switchToFirstAttempts();
              }}
              type="button"
            >
              First
            </button>
          </div>

          <div className="flex h-10 shrink-0 items-center gap-2">
            {type === ElementType.TEST && (
              <Autocomplete
                value={selectedModules}
                className="h-10 w-28 [&>div]:border [&>div]:border-[#dfe1e5] [&>div]:shadow-sm"
                placeholder={'Module'}
                displayText="Module"
                searchable={false}
                options={modulesOptions}
                setValue={onChangeModulesColumns}
              />
            )}

            <Autocomplete
              value={columns}
              className="h-10 w-28 [&>div]:border [&>div]:border-[#dfe1e5] [&>div]:shadow-sm"
              placeholder={'Columns'}
              displayText="Columns"
              searchable={false}
              options={columnsOptions}
              setValue={onChangeColumns}
            />
          </div>
        </div>

        <div className="flex min-w-0 items-center gap-3">
          <div className="w-[22rem] shrink-0">
            <ElementSelect
              type={type}
              onChange={onChangeElement}
              onChangeType={onChangeType}
              courseId={currentCourseId}
            />
          </div>

          <div className="w-[17.5rem] shrink-0">
            <TypeSelect value={type} onChange={onChangeType} />
          </div>
        </div>
      </section>

      <hr className={'my-1 w-full opacity-10'} />

      <section className={'text-sm'}>
        {type === ElementType.QUIZ && (
          <Table
            isLoading={false}
            isEmpty={!quizQuestionsSort}
            sortKeys={[]}
            sort={{}}
            setSort={() => {}}
            tableClassName="w-auto"
            columns={[
              { title: '#', key: '#' },
              ...orderedColumns.map((item) => ({
                title: getColumnTitle(item),
                key: item,
                className: getColumnClassName(item),
              })),
              ...getUsersColumns(),
            ]}
          >
            {quizQuestionsSort?.map((item, index) => {
              const question = item.question;
              const correctChoice = question?.choices.find(
                (choice) => choice.correct,
              );

              const answer =
                question && correctChoice
                  ? question?.answerType === QuestionAnswerType.TextEntry
                    ? correctChoice.text
                    : orderToLetter(correctChoice.order)
                  : '';

              const performanceData = quizPerformance.find(
                (item) => item.questionId === question?.id,
              );

              return (
                <tr key={item.order}>
                  <td>{item.order + 1}</td>
                  {orderedColumns.map((col) => (
                    <Fragment key={col}>
                      {col === 'QID' && <td>{question?.id}</td>}
                      {col === 'Domain' && <td>{question?.domain?.name}</td>}
                      {col === 'Skill' && <td>{question?.skill?.name}</td>}
                      {col === 'Type' && <td>{question?.type1?.name}</td>}
                      {col === 'Subject' && <td>{question?.subject?.name}</td>}
                      {col === 'Ans.' && <td className="text-center">{answer}</td>}
                      {col === 'Performance' && (
                        <td
                          className="text-center"
                          style={{
                            backgroundColor: performanceColor(
                              performanceData?.performance,
                            ),
                          }}
                        >
                          {formatPercent(performanceData?.performance)}
                        </td>
                      )}
                    </Fragment>
                  ))}

                  {orderedUsers?.map((user) => {
                    const userId = Number(getAutocompleteKey(user));

                    if (!userId) return null;
                    const attempt = quizAttempts.find(
                      (a) => a.userId === userId,
                    );

                    if (!attempt) {
                      return (
                        <td
                          key={`${userId}${index}`}
                          className={userAnswerCellClassName}
                        ></td>
                      );
                    }

                    const answer = quizAttempts
                      .find((i) => i.userId === userId)
                      ?.answers.find((q) => q.id === Number(question?.id));
                    return (
                      <td
                        key={`${userId}${index}`}
                        className={`${userAnswerCellClassName} ${
                          answer?.isCorrect ? 'text-green' : 'text-red'
                        }`}
                      >
                        {answer?.answer}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </Table>
        )}

        {type === ElementType.TEST && (
          <Table
            isLoading={false}
            isEmpty={!test}
            sortKeys={[]}
            sort={{}}
            setSort={() => {}}
            tableClassName="w-auto"
            columns={[
              { title: '#', key: '#' },
              ...orderedColumns.map((item) => ({
                title: getColumnTitle(item),
                key: item,
                className: getColumnClassName(item),
              })),
              ...getUsersColumns(),
            ]}
          >
            {test?.sections?.map((section, sectionIndex) => {
              return section.modules.map((module, moduleIndex) => {
                if (!module.questions.length) {
                  return null;
                }
                if (
                  !selectedModules?.includes(
                    `${module.sectionName} - ${module.level}`,
                  )
                )
                  return null;
                return (
                  <Fragment key={section.name + module.level}>
                    <tr>
                      <td
                        colSpan={99}
                        className={getModuleHeaderClassName(module.sectionName)}
                      >
                        {module.sectionName + ' - ' + module.level}
                      </td>
                    </tr>
                    {module.questions.map((question, index) => {
                      return (
                        <tr key={question.id + module.level}>
                          <td>{index + 1}</td>
                          {orderedColumns.map((col) => {
                            const performanceData = testPerformance.find(
                              (item) =>
                                item.questionId === question.id &&
                                module.level === item?.moduleLevel,
                            );
                            return (
                              <Fragment key={col}>
                                {col === 'QID' && <td>{question.id}</td>}
                                {col === 'Domain' && (
                                  <td>{question?.domain}</td>
                                )}
                                {col === 'Skill' && <td>{question?.skill}</td>}
                                {col === 'Type' && <td>{question?.type}</td>}
                                {col === 'Subject' && (
                                  <td>{question?.subject}</td>
                                )}
                                {col === 'Ans.' && (
                                  <td className="text-center">{question.answer}</td>
                                )}
                                {col === 'Performance' && (
                                  <td
                                    className="text-center"
                                    style={{
                                      backgroundColor: performanceColor(
                                        performanceData?.performance,
                                      ),
                                    }}
                                  >
                                    {formatPercent(
                                      performanceData?.performance,
                                    )}
                                  </td>
                                )}
                              </Fragment>
                            );
                          })}

                          {orderedUsers?.map((user) => {
                            const userId = Number(getAutocompleteKey(user));
                            const attempt = testAttempts.find(
                              (attempt) => attempt.userId === userId,
                            );
                            const answer = attempt?.answers.find(
                              (item) => item.id === question.id,
                            );
                            const isMathAdaptiveLevelWrong =
                              module.sectionName === 'math' &&
                              module.level !== TestSectionModuleLevel.All &&
                              TestAttemptModuleLevel[module.level] !==
                                attempt?.mathModuleLevel;
                            const isReadingAdaptiveLevelWrong =
                              module.sectionName === 'reading and writing' &&
                              module.level !== TestSectionModuleLevel.All &&
                              TestAttemptModuleLevel[module.level] !==
                                attempt?.rwModuleLevel;

                            if (
                              !answer ||
                              isMathAdaptiveLevelWrong ||
                              isReadingAdaptiveLevelWrong
                            ) {
                              return (
                                <td
                                  key={`${userId}${sectionIndex}${moduleIndex}${index}`}
                                  className={userAnswerCellClassName}
                                ></td>
                              );
                            }
                            return (
                              <td
                                key={`${userId}${sectionIndex}${moduleIndex}${index}`}
                                className={`${userAnswerCellClassName} ${
                                  answer.isCorrect ? 'text-green' : 'text-red'
                                }`}
                              >
                                {answer.answer}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </Fragment>
                );
              });
            })}
          </Table>
        )}

        {!quizQuestionsSort?.length && !test && (
          <p className={'mt-8 text-center text-blue'}>Select quiz or test</p>
        )}
      </section>
    </div>
  );
};
export default AnswersView;
